import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LinearProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import { usePermissions } from '../../permissions';
import { useActiveExternalRole } from '../../permissions/external-roles';
import { NAVBAR_HEIGHT, SIDEBAR_WIDTH_COLLAPSED } from '../constants/layout';
import { EventPanel, getHiddenPanelParent, getPanelUri, panelGroups } from '../panels';

const DRAWER_WIDTH = 248;

const getInnerComponentType = (el) => (el?.props?.children ? el.props.children.type : el?.type);

function SideBar({ isOpen, onToggle, annProcessing }) {
    const theme = useTheme();
    const location = useLocation();
    const isDark = theme.palette.mode === 'dark';
    const { hasPermission: userHasPermission } = usePermissions();
    const externalRole = useActiveExternalRole();

    // Per-group open/close state, persisted across refreshes
    const [expandedGroups, setExpandedGroups] = React.useState(() => {
        try {
            const saved = localStorage.getItem('sidebarExpandedGroups');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    // Whether the sidebar was opened by clicking a group icon while collapsed.
    // If true, clicking a nav link will collapse the sidebar again.
    const [openedFromCollapsed, setOpenedFromCollapsed] = React.useState(false);

    // When sidebar is explicitly toggled from the navbar, clear the flag
    React.useEffect(() => {
        if (!isOpen) setOpenedFromCollapsed(false);
    }, [isOpen]);

    const toggleGroup = (label) => {
        setExpandedGroups((prev) => {
            const next = { ...prev, [label]: prev[label] === true ? false : true };
            localStorage.setItem('sidebarExpandedGroups', JSON.stringify(next));
            return next;
        });
    };

    // Open a specific group (used when clicking a group icon from collapsed state)
    const openGroup = (label) => {
        setExpandedGroups((prev) => {
            const next = { ...prev, [label]: true };
            localStorage.setItem('sidebarExpandedGroups', JSON.stringify(next));
            return next;
        });
    };

    // Called when a nav link is clicked in the expanded-from-collapsed state
    const handleLinkClick = (groupLabel) => {
        if (openedFromCollapsed) {
            if (groupLabel) {
                setExpandedGroups((prev) => {
                    const next = { ...prev, [groupLabel]: false };
                    localStorage.setItem('sidebarExpandedGroups', JSON.stringify(next));
                    return next;
                });
            }
            setOpenedFromCollapsed(false);
            onToggle();
        }
    };

    // Build the URI for a sidebar panel (Events gets a default date param)
    const getSidebarUri = (panel) => {
        const extra = getInnerComponentType(panel.component) === EventPanel ? { start_date: 'default' } : {};
        return getPanelUri(panel.component, {}, extra);
    };

    // Returns true when a panel's route matches the current location
    const normalize = (uri) => (uri?.endsWith('/') && uri !== '/' ? uri.slice(0, -1) : uri);

    const isSelected = (panel) => {
        const panelUri = normalize(getSidebarUri(panel).split('?')[0]);
        const currentPath = normalize(location.pathname);
        const hiddenParent = getHiddenPanelParent(currentPath);
        if (hiddenParent) {
            return panelUri === normalize(getPanelUri(hiddenParent.component));
        }
        return currentPath === panelUri;
    };

    const sidebarShowsPermission = (requiredPermission) => {
        if (!requiredPermission) return true;
        return userHasPermission(requiredPermission);
    };

    /** Panels the user may see in a collapsible group (e.g. Snapshots without Builder when view-only). */
    const visibleGroupPanels = (group) => group.panels.filter((p) => sidebarShowsPermission(p.requiredPermission));

    // External-only users are confined to whatever panels their role declares
    // visible (see EXTERNAL_ROLES). Anything else would just bounce them back
    // via the route guard, so we hide it entirely to avoid the temptation.
    const visiblePanelGroups = externalRole
        ? panelGroups
              .map((group) => {
                  if (group.standalone) {
                      return externalRole.isPanelVisible(group.panel) ? group : null;
                  }
                  const allowed = group.panels.filter(externalRole.isPanelVisible);
                  return allowed.length > 0 ? { ...group, panels: allowed } : null;
              })
              .filter(Boolean)
        : panelGroups;

    // Drawer transition styles.
    const drawerSx = {
        position: 'fixed', // out of flex flow — siblings never reflow
        zIndex: theme.zIndex.drawer,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        '& .MuiDrawer-paper': {
            borderRight: 'none',
            backgroundColor: theme.palette.common.navSurface,
            marginTop: `${NAVBAR_HEIGHT}px`,
            overflowX: 'hidden',
        },
        ...(isOpen
            ? {
                  width: DRAWER_WIDTH,
                  transition: theme.transitions.create('width', {
                      easing: theme.transitions.easing.easeInOut,
                      duration: 240,
                  }),
                  '& .MuiDrawer-paper': {
                      width: DRAWER_WIDTH,
                      borderRight: 'none',
                      backgroundColor: theme.palette.common.navSurface,
                      marginTop: `${NAVBAR_HEIGHT}px`,
                      transition: theme.transitions.create('width', {
                          easing: theme.transitions.easing.easeInOut,
                          duration: 240,
                      }),
                  },
              }
            : {
                  width: `${SIDEBAR_WIDTH_COLLAPSED}px`,
                  transition: theme.transitions.create('width', {
                      easing: theme.transitions.easing.easeInOut,
                      duration: 200,
                  }),
                  overflowX: 'hidden',
                  '& .MuiDrawer-paper': {
                      width: `${SIDEBAR_WIDTH_COLLAPSED}px`,
                      borderRight: 'none',
                      backgroundColor: theme.palette.common.navSurface,
                      marginTop: `${NAVBAR_HEIGHT}px`,
                      overflowX: 'hidden',
                      transition: theme.transitions.create('width', {
                          easing: theme.transitions.easing.easeInOut,
                          duration: 200,
                      }),
                  },
              }),
    };

    // Processing indicator shown under the Events icon
    const eventProgressSx = {
        height: '3px',
        borderRadius: '2px',
        backgroundColor: 'rgba(0,0,0,0)',
        '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.events.processing,
        },
    };

    // Icon with an optional processing bar (used for Events)
    const renderIcon = (panel) => {
        const showProgress = annProcessing && panel.annotationProcessingEvents;
        if (!showProgress) return panel.icon;
        return (
            <Box position='relative' display='inline-flex'>
                {panel.icon}
                <Box position='absolute' bottom={-4} right={0} width='100%'>
                    <LinearProgress sx={eventProgressSx} />
                </Box>
            </Box>
        );
    };

    // Shared selected-item accent styling.
    // borderRadius: 2 is repeated here so the sx-injected rule overrides the theme's
    // higher-specificity `&.Mui-selected { borderRadius: 8px }` styleOverride,
    // keeping the selected background corners identical to the hover background corners.
    const selectedSx = {
        '&.Mui-selected': {
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.14 : 0.1),
            color: 'primary.main',
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            '&:hover': {
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.15),
            },
        },
    };

    // Width of the fixed-position icon container.
    // collapsed drawer = SIDEBAR_WIDTH_COLLAPSED (52px), mx = 0.75 × 2 = 12px → button = 40px.
    // Keeping this constant ensures icons never shift during open/close.
    const ICON_BOX_PX = SIDEBAR_WIDTH_COLLAPSED - 12;

    // Label text fades in after the drawer starts opening, fades out immediately on close.
    const textSx = {
        opacity: isOpen ? 1 : 0,
        transition: isOpen ? 'opacity 160ms ease 80ms' : 'opacity 100ms ease',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        m: 0,
        minWidth: 0,
    };

    // Base styles shared by every top-level list item.
    // overflow:'hidden' clips the MUI hover/ripple background to the borderRadius,
    // ensuring hover and selected states have the same rounded corners.
    const itemBaseSx = {
        minHeight: 42,
        borderRadius: 2,
        overflow: 'hidden',
        mx: 0.75,
        my: 0.25,
        px: 0,
        '& .MuiSvgIcon-root': { fontSize: 20 },
    };

    // Single unified list — no DOM swap on toggle, so icons never move.
    const renderList = () => (
        <List disablePadding sx={{ pt: 0.5 }}>
            {visiblePanelGroups.map((group) => {
                if (!sidebarShowsPermission(group.requiredPermission)) return null;

                if (group.standalone) {
                    const panel = group.panel;
                    if (!sidebarShowsPermission(panel.requiredPermission)) return null;
                    return (
                        <Tooltip
                            key={panel.uri}
                            title={panel.name}
                            placement='right'
                            arrow
                            disableHoverListener={isOpen}
                        >
                            <ListItemButton
                                component={NavLink}
                                to={getSidebarUri(panel)}
                                selected={isSelected(panel)}
                                data-testid={`sidebar-link-${
                                    panel.uri === '/' ? 'home' : panel.uri.replace(/\//g, '-').replace(/^-/, '')
                                }`}
                                onClick={() => handleLinkClick(null)}
                                sx={{ ...itemBaseSx, ...selectedSx }}
                            >
                                {/* Fixed-width icon box — pixel position never changes */}
                                <Box
                                    sx={{
                                        width: ICON_BOX_PX,
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                                        {renderIcon(panel)}
                                    </ListItemIcon>
                                </Box>
                                {/* Label fades in/out without touching the icon */}
                                <ListItemText
                                    primary={panel.name}
                                    primaryTypographyProps={{
                                        variant: 'body2',
                                        fontWeight: 500,
                                        noWrap: true,
                                    }}
                                    sx={textSx}
                                />
                            </ListItemButton>
                        </Tooltip>
                    );
                }

                const panelsInGroup = visibleGroupPanels(group);
                if (panelsInGroup.length === 0) return null;

                const groupOpen = expandedGroups[group.groupLabel] === true;
                const active = panelsInGroup.some((p) => isSelected(p));
                return (
                    <React.Fragment key={group.groupLabel}>
                        <Tooltip title={group.groupLabel} placement='right' arrow disableHoverListener={isOpen}>
                            <ListItemButton
                                data-testid={`sidebar-group-${group.groupLabel.toLowerCase().replace(/\s+/g, '-')}`}
                                onClick={() => {
                                    if (!isOpen) {
                                        openGroup(group.groupLabel);
                                        setOpenedFromCollapsed(true);
                                        onToggle();
                                    } else {
                                        toggleGroup(group.groupLabel);
                                    }
                                }}
                                sx={{
                                    ...itemBaseSx,
                                    bgcolor: active
                                        ? alpha(theme.palette.primary.main, isDark ? 0.1 : 0.06)
                                        : 'transparent',
                                    '&:hover': {
                                        bgcolor: active
                                            ? alpha(theme.palette.primary.main, isDark ? 0.16 : 0.1)
                                            : alpha(theme.palette.action.hover, 0.06),
                                    },
                                }}
                            >
                                {/* Fixed-width icon box */}
                                <Box
                                    sx={{
                                        width: ICON_BOX_PX,
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            justifyContent: 'center',
                                            color: active ? 'primary.main' : 'text.secondary',
                                        }}
                                    >
                                        {group.icon}
                                    </ListItemIcon>
                                </Box>
                                {/* Label + chevron fade in/out */}
                                <Box
                                    sx={{
                                        ...textSx,
                                        display: 'flex',
                                        alignItems: 'center',
                                        flex: 1,
                                    }}
                                >
                                    <ListItemText
                                        primary={group.groupLabel}
                                        primaryTypographyProps={{
                                            variant: 'body2',
                                            fontWeight: active ? 600 : 500,
                                            color: active ? 'primary.main' : 'text.primary',
                                            letterSpacing: '0.01em',
                                            noWrap: true,
                                        }}
                                        sx={{ m: 0, flex: 1, overflow: 'hidden' }}
                                    />
                                    <ExpandMoreIcon
                                        fontSize='small'
                                        sx={{
                                            opacity: 0.4,
                                            flexShrink: 0,
                                            ml: 0.5,
                                            mr: 1,
                                            transition: 'transform 0.25s ease',
                                            transform: groupOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        }}
                                    />
                                </Box>
                            </ListItemButton>
                        </Tooltip>
                        <Collapse in={isOpen && groupOpen} timeout={200} unmountOnExit>
                            <List disablePadding>
                                {panelsInGroup.map((panel) => (
                                    <ListItemButton
                                        key={panel.uri}
                                        component={NavLink}
                                        to={getSidebarUri(panel)}
                                        selected={isSelected(panel)}
                                        data-testid={`sidebar-link-${panel.uri.replace(/\//g, '-').replace(/^-/, '')}`}
                                        onClick={() => handleLinkClick(group.groupLabel)}
                                        sx={{
                                            pl: 5.5,
                                            minHeight: 38,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            mx: 0.75,
                                            my: 0.2,
                                            ...selectedSx,
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 32,
                                                opacity: 0.7,
                                                '& .MuiSvgIcon-root': { fontSize: 18 },
                                            }}
                                        >
                                            {panel.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={panel.name}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                fontSize: '0.8rem',
                                            }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Collapse>
                    </React.Fragment>
                );
            })}
        </List>
    );

    // ─────────────────────────────────────────────────────────────────
    return (
        <Drawer variant='permanent' sx={drawerSx} data-testid='mimir-sidebar'>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flex: 1, overflowX: 'hidden', overflowY: 'auto' }}>{renderList()}</Box>
                <Box
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        py: 0.5,
                    }}
                >
                    <Tooltip title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'} placement='right' arrow>
                        <ListItemButton
                            onClick={onToggle}
                            aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                            sx={{
                                justifyContent: isOpen ? 'flex-end' : 'center',
                                minHeight: 40,
                                borderRadius: 2,
                                overflow: 'hidden',
                                mx: 0.75,
                                my: 0.25,
                                px: isOpen ? 1.5 : 1,
                                '& .MuiSvgIcon-root': { fontSize: 20 },
                            }}
                        >
                            {isOpen ? (
                                <ChevronLeftIcon sx={{ color: 'text.secondary' }} />
                            ) : (
                                <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                            )}
                        </ListItemButton>
                    </Tooltip>
                </Box>
            </Box>
        </Drawer>
    );
}

SideBar.propTypes = {
    isOpen: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
    annProcessing: state.health.annProcessing || false,
});

export default connect(mapStateToProps)(SideBar);
